import MultiRadio from './MultiRadio';
import { useState, useEffect, useRef } from 'react';

export default function RadioTable({ onChange, options, colLabels, rowLabels }) {
  const columns = colLabels ?? ['Column1', 'Column2', 'Column3'];

  const rows = rowLabels ?? ['Row1', 'Row2', 'Row3'];

  const opts = options ?? [
    [
      { value: 'en', label: 'English' },
      { value: 'pt', label: 'Português' },
      { value: 'al', label: 'Alemao' },
    ],
    [
      { value: 'bn', label: 'Banana' },
      { value: 'ap', label: 'Apple' },
      { value: 'lm', label: 'Lemon' },
    ],
    [
      { value: { number: 0, label: 'on' }, label: 'One' },
      { value: { number: 1, label: 'tw' }, label: 'Two' },
      { value: { number: 2, label: 'tr' }, label: 'Three' },
    ],
  ];

  const rowsCount = opts.length;

  const [selectedPerRow, setSelectedPerRow] = useState(() =>
    Array.from({ length: rowsCount }).map(() => null),
  );
  const lastEmittedRef = useRef(null);

  const deepEq = (a, b) => {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return a === b;
    }
  };

  useEffect(() => {
    setSelectedPerRow((prev) => {
      const next = Array.from({ length: rowsCount }).map((_, i) => prev[i] ?? null);

      // shallow compare -> use deep equality now
      const same = prev.length === next.length && next.every((v, i) => deepEq(prev[i], v));

      return same ? prev : next;
    });
  }, [rowsCount]);

  const handleRowChange = (rowIndex, value) => {
    setSelectedPerRow((prev) => {
      if (deepEq(prev[rowIndex], value)) return prev; // no change
      const next = [...prev];
      next[rowIndex] = value;
      return next;
    });
  };

  useEffect(() => {
    if (typeof onChange !== 'function') return;

    const prevEmitted = lastEmittedRef.current;
    const next = selectedPerRow;

    const equal =
      Array.isArray(prevEmitted) &&
      prevEmitted.length === next.length &&
      next.every((v, i) => deepEq(prevEmitted[i], v));

    if (!equal) {
      // store a shallow copy (objects inside remain as provided)
      lastEmittedRef.current = next.slice();
      onChange(next);
    }
  }, [selectedPerRow, onChange]);

  return (
    <div className="flex flex-col gap-2 w-full text-sm">
      {/* Header row with column labels */}
      <div className="flex w-full border-b-2 border-gray-300 pb-2">
        {/* Empty space for row labels */}
        <div className="w-48 shrink-0" />
        {/* Column headers - evenly distributed */}
        <div className="flex flex-1 justify-around text-center text-xs">
          {columns.map((label, colIndex) => (
            <div key={colIndex} className="flex-1 px-1">
              {label}
            </div>
          ))}
        </div>
      </div>

      {/* Data rows */}
      <div className="flex flex-col gap-3">
        {opts.map((rowOptions, rowIndex) => (
          <div key={rowIndex} className="flex items-center w-full">
            {/* Row label */}
            <div className="w-48 shrink-0 pr-4 text-gray-600 text-sm">{rows[rowIndex]}</div>

            {/* Radio buttons */}
            <div className="flex-1">
              <MultiRadio
                name={`row-${rowIndex}`}
                layout="horizontal"
                options={rowOptions}
                isTable={true}
                showLabels={false}
                selected={selectedPerRow[rowIndex]}
                onChange={(v) => handleRowChange(rowIndex, v)}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
